import { CoachIsEnumType } from 'components/molecules/CoachSelector';
import { Constants as AnamnesisConstants } from 'defs/ga/fields/anamnesis';
import { warn } from 'helper/debug';
import config from 'helper/mapBackendConfig';
import { CoachType } from 'types/coach';
import { DateTimeType } from 'types/DateTime';
import { TargetType } from 'types/NotificationTargets';

/*
 * Levels
 */
export type EventLevelsValues = 'NA' | 'BRONZE' | 'SILVER' | 'GOLD';

export const NumericalEventLevels = {
	NA: 0,
	BRONZE: 1,
	SILVER: 2,
	GOLD: 3,
};

export const EventLevels = {
	NotAvailable: 'NA',
	Bronze: 'BRONZE',
	Silver: 'SILVER',
	Gold: 'GOLD',
};

/*
 * EventTypes
 */
export type EventTypesValues =
	| 'POINT'
	| 'BADGE'
	| 'STICKER'
	| 'FEEDBACK'
	| 'WARNING'
	| 'IMAGE'
	| 'LEVEL_UP'
	| 'MESSAGE'
	| 'PROGRAM_SUMMARY';

export const EventTypes = {
	Point: 'POINT',
	Badge: 'BADGE',
	Sticker: 'STICKER',
	Feedback: 'FEEDBACK',
	Warning: 'WARNING',
	LevelUp: 'LEVEL_UP',
	Image: 'IMAGE',
	Message: 'MESSAGE',
	ProgramSummary: 'PROGRAM_SUMMARY',
};

/*
 * DTOs
 */
export type EventDto = {
	callback?: Function;
	eventLevel: EventLevelsValues;
	code: string;
	id?: string;
	eventType: EventTypesValues;
	bonusPoints?: number;
	points?: number;
	attributes?: {
		level: string;
		threshold: string;
	};
	awardedDateTime?: DateTimeType | null;
	infoText?: string;
	overwrites?: {
		title?: string;
		text?: string;
		image?: string;
		illustrationMarkup?: React.ReactNode;
		coach?: CoachType;
		targets?: TargetType | void;
	};
	modifier?: string;
	fameBadge?: any; // JSX
	payload?: any;
};

export type EventDtoEnhanced = {
	reactId: string; // will be used for unique identification (flushing),
	payload?: Object; // Optional: provide more data i.e. a programId
};

export type EventProgramSummaryDto = EventDto & {
	percentageFinished: number;
	totalTodos: number;
	totalTodosDone: number;
	numberOfWorkouts: number;
	numberOfWorkoutsDone: number;
	numberOfRecipes: number;
	numberOfRecipesDone: number;
	numberOfTodos: number;
	numberOfTodosDone: number;
	numberOfTipsDone: number;
	numberDoneTips: number;
	calories: number;
	weightLost: number;
	badges: EventDto[];
	longestRun: number;
};

export type EventCustomOverlayDto = EventDto & EventDtoEnhanced;

/*
 * Event Codes
 */
export const EventFeedbackCodes = {
	F01: 'F01',
	F03: 'F03',
	F04: 'F04',
	F05: 'F05',
	F06: 'F06',
	F07: 'F07',
	F08: 'F08',
	F09: 'F09',
	F10: 'F10',
	F11: 'F11',
	F12: 'F12',
};

export const EventValidationCodes = {
	F07: 'error.validation.f07',
	F08: 'error.validation.f08',
};

export const EventWarningCodes = {
	W01: 'W01',
	W02: 'W02',
};

export const EventLevelUpCodes = {
	Z01: 'Z01',
};

export const EventBadgeCodes = {
	B01: 'B01',
	B01_image: 'award/B01-1',
	B02: 'B02',
	B03: 'B03',
	B04: 'B04',
	B05: 'B05',
	B06: 'B06',
	B07: 'B07',
	B08: 'B08',
	B09: 'B09',
	B11: 'B11',
	B12: 'B12',
	B13: 'B13',
	B14: 'B14',
	B15: 'B15',
	B16: 'B16',
	B17: 'B17',
	B18: 'B18',
	B19: 'B19',
	B20: 'B20',
	B21: 'B21',
	B22: 'B22',
	B23: 'B23',
	B24: 'B24',
	B25: 'B25',
	B26: 'B26',
};

export const EventStickerCodes = {
	S01: 'S01',
	S02: 'S02',
	S03: 'S03',
	S04: 'S04',
	S05: 'S05',
};

/*
 * Event Custom Codes
 * - Custom Codes: Codes from React that are not defined somewhere else i.e. in the backend
 */
export const EventCustomWarningCodes = {
	ProgramFeedLoading: 'ProgramFeedLoading',
	ProfileLoading: 'ProfileLoading',
};

export const EventCustomFeedbackCodes = {
	Error500: 'Error500', // Concept: must be Feedback, not Warning
	Error408: 'RequestTimeoutReached', // Concept: Must be Feedback, not Warning
	ValidationError: 'ValidationError', // Concept: must be Feedback, not Warning
	LoginLeaving: 'LoginLeaving',
	Offline: 'Offline',
	Dsgvo: 'Dsgvo',
	DsgvoReminder: 'DsgvoReminder',
	Z02: 'Z02', // PROGRAM_CANCELED
	CustomOverlay: 'CustomOverlay',
};

export const EventImageCodes = {
	AppStore: 'AppStore',
	Newsletter: 'Newsletter',
};

export const EventGestureCodes = {
	[EventImageCodes.AppStore]: 'bookmark',
	[EventImageCodes.Newsletter]: 'mail',

	[EventFeedbackCodes.F01]: 'front-friendly', // Wrong image => UI
	[EventFeedbackCodes.F03]: 'front-reflect',
	[EventFeedbackCodes.F04]: 'front-reflect', // Wrong image => UI
	[EventFeedbackCodes.F04]: 'front-reflect',
	[EventFeedbackCodes.F06]: 'front-friendly', // Wrong image => UI
	[EventFeedbackCodes.F07]: 'front-phone',
	[EventFeedbackCodes.F08]: 'front-phone',
	[EventFeedbackCodes.F09]: 'front-loose',
	[EventFeedbackCodes.F10]: 'front-friendly', // Wrong image => UI
	[EventFeedbackCodes.F11]: 'front-reflect',
	[EventFeedbackCodes.F12]: 'front-friendly', // Wrong image => UI

	[EventCustomFeedbackCodes.CustomOverlay]: 'front-indicate-friendly',
	[EventCustomFeedbackCodes.Error500]: 'front-reflect', // Wrong image => UI
	[EventCustomFeedbackCodes.Error408]: 'front-reflect', // Wrong image => UI
	[EventCustomFeedbackCodes.LoginLeaving]: 'front-reflect', // Wrong image => UI
	[EventCustomFeedbackCodes.Z02]: 'front-reflect', // Wrong image => UI
	[EventCustomFeedbackCodes.ValidationError]: 'front-reflect', // Wrong image => UI
	[EventCustomFeedbackCodes.Offline]: 'front-reflect', // TODO: Adjust
	[EventCustomFeedbackCodes.Dsgvo]: 'front-hello', // TODO: Adjust

	[EventWarningCodes.W01]: 'front-reflect', // Wrong image => UI
	[EventWarningCodes.W02]: 'front-indicate-friendly',

	[EventCustomWarningCodes.ProgramFeedLoading]: 'front-reflect', // Wrong image => UI
	[EventCustomWarningCodes.ProfileLoading]: 'front-reflect', // Wrong image => UI
};

export const EVENT_TYPE_DISABLE_CLOSE_MAP = {
	[EventTypes.Warning]: true,
	[EventTypes.ProgramSummary]: true,
};

export const EVENT_CODE_DISABLE_CLOSE_MAP = {
	[EventCustomFeedbackCodes.Error500]: true,
	[EventCustomFeedbackCodes.Error408]: true,
	[EventCustomFeedbackCodes.Offline]: true,
	[EventCustomFeedbackCodes.Dsgvo]: true,
	[EventCustomFeedbackCodes.DsgvoReminder]: true,
};

export type EventStyleType = 'full' | 'notification';

export type EventContentStyleMapType = {
	[key: string]: string; // EventStyleType,
};

export const EVENT_CONTENT_STYLE_DEFAULT = 'notification';

export const EVENT_CONTENT_STYLE_MAP: EventContentStyleMapType = {
	[EventTypes.ProgramSummary]: 'full',
};

export const EventImageSrcFolders = {
	AWARD: 'award',
	COACH: 'coach',
	IMAGE: 'notification/image',
};

export const getCoachBasedPostfix = (coach?: CoachIsEnumType) =>
	coach ? coach.toLowerCase() : AnamnesisConstants.Trainer.Mia.toLowerCase();

export const getEventImageSrc = (
	code: string,
	eventType: EventTypesValues,
	eventLevel: string,
	coach?: CoachIsEnumType
) => {
	let folderName = '';
	const name = [code];
	const coachName = getCoachBasedPostfix(coach);

	switch (eventType) {
		case EventTypes.Badge:
		case EventTypes.Sticker:
		case EventTypes.LevelUp:
			folderName = EventImageSrcFolders.AWARD;
			if (eventLevel && NumericalEventLevels[eventLevel] !== undefined) {
				name.push(NumericalEventLevels[eventLevel]);
			} else {
				warn(`Cannot get event-image: eventLevel "${eventLevel}" is not defined`);
			}
			break;
		case EventTypes.Feedback:
		case EventTypes.Warning:
			folderName = EventImageSrcFolders.COACH;
			if (EventGestureCodes[code]) {
				name[0] = `${EventGestureCodes[code].toLowerCase()}-${coachName}`;
			} else {
				warn(`Cannot get event-image: EventGestureCode "${code}" is not defined`);
				name[0] = `front-friendly-${coachName}`;
			}
			break;
		case EventTypes.Image:
			folderName = EventImageSrcFolders.IMAGE;
			if (code === EventImageCodes.AppStore) {
				name[0] = `${EventGestureCodes[code].toLowerCase()}-${coachName === 'mia' ? 'f' : 'm'}`;
			} else if (EventGestureCodes[code]) {
				name[0] = `${EventGestureCodes[code].toLowerCase()}`;
			} else {
				warn(`Cannot get event-image: EventGestureCode "${code}" is not defined`);
			}
			break;
		default:
			warn(`Cannot get event-image: eventType "${eventType}" is not defined`);
			break;
	}

	return `${config.env.baseUrlUi}/assets/img/svg/${folderName}/${name.join('-')}.svg`;
};
